/**
 * Real-Time Schedule Utils
 *
 * currentPeriod is purely a function of wall-clock time given a school's
 * periods/buffers (see @hallpass/schedule's resolveSchedule, whose windowed
 * period-matching is mirrored locally in findCurrentPeriod below), so this
 * fetches GET /schedule/today once per org and then recomputes locally on a
 * timer -- no repeat network polling. It only refetches when something
 * server-side actually changed the schedule's *inputs* (a calendar/period/
 * schedule-type mutation, pushed over the passes-api Socket.io connection as
 * 'schedule:changed' -- see apps/passes-api/src/lib/socket.ts and
 * apps/schools-api/src/lib/scheduleEvents.ts) or when the school-local date
 * rolls over.
 */

import { standardizePeriodFormat, timeToMinutes } from './periodFormatHelper';
import { getTodaySchedule } from '../api/schoolConfigApi';
import { getCurrentTimeInTimezone, getTodayInTimezone } from '@hallpass/schedule';
import { io, type Socket } from 'socket.io-client';
import { PASSES_API_BASE } from '../api/config';
import { getToken } from '../api/tokenStore';

// An org id may arrive as a number or a string; it is always normalized to a
// string map key via String(orgId).
type OrgId = string | number;

// One period window as consumed here: the wall-clock start/end plus the
// buffered window bounds ("HH:MM" strings) that findCurrentPeriod matches
// against. GET /schedule/today returns more fields (id, scheduleTypeId, ...);
// only these are read.
interface SchedulePeriod {
  name: string;
  startTime: string;
  endTime: string;
  windowStart: string;
  windowEnd: string;
}

// The active schedule variant for the day, keyed by its id in the variants map.
interface ScheduleVariant {
  id: OrgId;
  name: string;
}

// The last-fetched GET /schedule/today response, reduced to what the local
// recompute needs.
interface RawSchedule {
  date: string | null;
  timezone: string | null;
  scheduleType: ScheduleVariant | null;
  periods: SchedulePeriod[];
}

// Derived period-state delivered to period subscribers (null when no period is
// currently in session).
interface PeriodData {
  period: string | null;
  abDay: null;
  scheduleType: string | null;
  periodInfo: {
    timeRemaining: number;
    start: string;
    end: string;
  };
  isInSession: boolean;
}

// Schedule variants delivered to variant subscribers, keyed by scheduleType id.
type Variants = Record<string, ScheduleVariant>;

// The second callback arg is the fetch error, if any -- it can be any thrown
// value, so it stays `unknown`.
type PeriodCallback = (periodData: PeriodData | null, error?: unknown) => void;
type VariantCallback = (variants: Variants, error?: unknown) => void;

// Shared per-org poller state (see orgPollers below).
interface PollerEntry {
  orgId: OrgId;
  periodSubscribers: PeriodCallback[];
  variantSubscribers: VariantCallback[];
  raw: RawSchedule | null;
  // true when the cached schedule (if any) can't be trusted -- no successful
  // fetch yet, or a refetch failed -- so the local tick refetches instead of
  // recomputing from it. See fetchAndNotify/tick.
  stale: boolean;
  tickIntervalId: ReturnType<typeof setInterval> | null;
}

// Payload of the 'schedule:changed' Socket.io push.
interface ScheduleChangedPayload {
  schoolId?: OrgId | null;
}

/** {allowed, reason} result of a pass-creation temporal check. */
interface PassCreationResult {
  allowed: boolean;
  reason: string;
}

// A "current period" as consumed by the pass-creation helpers below. This is a
// flatter shape than PeriodData -- it carries the period name/number, total
// length, and remaining minutes directly.
interface CurrentPeriodInput {
  period?: string | number | null;
  periodNumber?: string | number | null;
  minutes?: number | null;
  timeRemaining?: number | null;
}

// Hardcoded per-period-name blackout window (minutes blocked at the start/end).
interface BlackoutConfig {
  passBlockingStart: number;
  passBlockingEnd: number;
}

// Shared poller map: orgId -> { orgId, periodSubscribers, variantSubscribers,
// raw, tickIntervalId }. Period and schedule-variant data both come from the
// same GET /schedule/today response, so subscribeToPeriodUpdates and
// subscribeToScheduleVariants share one entry per org instead of each
// running its own -- otherwise every dashboard subscribing to both (or
// multiple dashboards subscribing to the same org) would multiply an
// identical request.
const orgPollers = new Map<string, PollerEntry>();

// Local recompute cadence -- no network call, just re-deriving currentPeriod
// from the already-fetched periods array. Matches TeacherDashboard's
// existing 30s local tick for consistency, not any external constraint.
const LOCAL_TICK_INTERVAL = 30 * 1000;

// Current state cache (updated on fetch and on every local tick)
const currentPeriodState = new Map<string, PeriodData | null>(); // orgId -> period state
const scheduleVariants = new Map<string, Variants>(); // orgId -> schedule variants

// Single shared connection for the 'schedule:changed' push -- the
// authenticated user is auto-joined server-side to their own school's
// broadcast room, so one connection covers every org this module ever
// actually gets subscribed to (always the caller's own school; see
// DevOverrideContext.js/StudentDashboard.js). Connected lazily on first
// subscription, torn down once the last org poller unsubscribes.
let scheduleSocket: Socket | null = null;

function ensureScheduleSocket(): void {
  if (scheduleSocket) return;

  scheduleSocket = io(PASSES_API_BASE, {
    auth: (cb) => cb({ token: getToken() }),
  });

  scheduleSocket.on('schedule:changed', (payload: ScheduleChangedPayload | undefined) => {
    const key = payload?.schoolId != null ? String(payload.schoolId) : null;
    if (!key) return;
    const entry = orgPollers.get(key);
    if (entry) fetchAndNotify(key, entry);
  });

  // Resync on (re)connect in case a schedule change was published while this
  // client was disconnected.
  scheduleSocket.on('connect', () => {
    orgPollers.forEach((entry, key) => fetchAndNotify(key, entry));
  });
}

function releaseScheduleSocketIfIdle(): void {
  if (orgPollers.size === 0 && scheduleSocket) {
    scheduleSocket.disconnect();
    scheduleSocket = null;
  }
}

// Mirrors @hallpass/schedule's resolveSchedule period-matching: buffered
// windows of adjacent periods can overlap, so order by startTime and take
// the earliest match, independent of input order. "HH:MM" strings compare
// correctly with <= because they're zero-padded and equal-length.
function findCurrentPeriod(
  periods: SchedulePeriod[],
  currentTime: string
): SchedulePeriod | null {
  const byStartTime = [...periods].sort((a, b) =>
    a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0
  );
  return (
    byStartTime.find(
      (p) => p.windowStart <= currentTime && currentTime <= p.windowEnd
    ) ?? null
  );
}

// Derives periodData/variants from entry.raw (the last-fetched schedule) as
// of *now*, and notifies subscribers. Safe to call from a fetch continuation
// or from the local tick -- it never touches the network itself.
function recomputeAndNotify(key: string, entry: PollerEntry): void {
  const raw = entry.raw;
  let periodData: PeriodData | null = null;

  if (raw?.timezone && raw.periods?.length) {
    const nowTimeStr = getCurrentTimeInTimezone(raw.timezone);
    const match = findCurrentPeriod(raw.periods, nowTimeStr);

    // GET /schedule/today doesn't return abDay, so periodData.abDay is
    // always null here -- a backend gap, not something fixable from the
    // frontend.
    if (match) {
      const nowMinutes = timeToMinutes(nowTimeStr);
      periodData = {
        period: standardizePeriodFormat(match.name),
        abDay: null,
        scheduleType: raw.scheduleType?.name || null,
        periodInfo: {
          timeRemaining: Math.max(0, timeToMinutes(match.endTime) - nowMinutes),
          start: match.startTime,
          end: match.endTime,
        },
        isInSession: true,
      };
    }
  }
  currentPeriodState.set(key, periodData);

  // hallpass-backend has no per-period abDay/passBlocking config.
  // ScheduleType does have schedule-wide startBuffer/endBuffer fields,
  // but isPassCreationAllowed always uses hardcoded per-period-name
  // blackout defaults (Advisory 5/5, 'Period N' 15/15, Lunch unlimited,
  // else 0/0) instead -- no admin UI currently sets startBuffer/endBuffer
  // (schoolConfigApi.js accepts them on create/update, but nothing
  // exposes them).
  const variants: Variants = {};
  if (raw?.scheduleType) {
    variants[raw.scheduleType.id] = {
      id: raw.scheduleType.id,
      name: raw.scheduleType.name,
    };
  }
  scheduleVariants.set(key, variants);

  entry.periodSubscribers.forEach((cb) => cb && cb(periodData));
  entry.variantSubscribers.forEach((cb) => cb && cb(variants));
}

async function fetchAndNotify(key: string, entry: PollerEntry): Promise<void> {
  try {
    const today = await getTodaySchedule(entry.orgId);
    entry.raw = {
      date: today?.date ?? null,
      timezone: today?.timezone ?? null,
      scheduleType: today?.scheduleType ?? null,
      periods: today?.periods ?? [],
    };
    // Fetch succeeded -- the cached schedule is fresh again, so the local
    // tick can resume recomputing from it instead of retrying the network.
    entry.stale = false;
    recomputeAndNotify(key, entry);
  } catch (error) {
    console.error(`❌ Error fetching schedule data for org ${entry.orgId}:`, error);

    // Mark the entry known-stale so the local tick refetches instead of
    // re-serving whatever is (or isn't) cached: an initial failure leaves
    // entry.raw null with nothing to recompute against, and a failed refetch
    // after a 'schedule:changed' push leaves entry.raw holding a schedule the
    // server has already superseded.
    entry.stale = true;

    // Only blank the cache / notify subscribers with the error when there is
    // no previously-good schedule to fall back on (i.e. the very first fetch
    // failed). On a refetch failure we keep serving the last known-good data
    // rather than flipping dashboards into a schedule-error state on a
    // transient blip -- the tick will retry and re-notify once it recovers.
    if (!entry.raw) {
      currentPeriodState.set(key, null);
      scheduleVariants.set(key, {});
      entry.periodSubscribers.forEach((cb) => cb && cb(null, error));
      entry.variantSubscribers.forEach((cb) => cb && cb({}, error));
    }
  }
}

// Local tick: recompute from the cached fetch, or refetch first if the
// school-local date has rolled over since that fetch (a stale `periods`
// array from yesterday can't be recomputed against -- it needs today's
// calendar entry).
function tick(key: string, entry: PollerEntry): void {
  const raw = entry.raw;

  // Retry path: no successful fetch yet (initial fetch failed, entry.raw is
  // null) or the cached schedule is known-stale (a refetch failed). Either
  // way, recomputing locally is pointless/wrong -- refetch instead. This is
  // the only retry the initial-failure case ever gets, since nothing else
  // re-triggers a fetch until a date rollover or a 'schedule:changed' push.
  if (!raw?.timezone || entry.stale) {
    fetchAndNotify(key, entry);
    return;
  }

  const today = getTodayInTimezone(raw.timezone);
  if (raw.date && today !== raw.date) {
    fetchAndNotify(key, entry);
    return;
  }
  recomputeAndNotify(key, entry);
}

function getOrCreatePoller(orgId: OrgId, key: string): PollerEntry {
  let entry: PollerEntry | undefined = orgPollers.get(key);
  if (entry) return entry;

  entry = {
    orgId,
    periodSubscribers: [],
    variantSubscribers: [],
    raw: null,
    // true when the cached schedule (if any) can't be trusted -- no successful
    // fetch yet, or a refetch failed -- so the local tick refetches instead of
    // recomputing from it. See fetchAndNotify/tick.
    stale: false,
    tickIntervalId: null,
  };

  orgPollers.set(key, entry);
  ensureScheduleSocket();
  fetchAndNotify(key, entry);
  entry.tickIntervalId = setInterval(() => tick(key, entry), LOCAL_TICK_INTERVAL);

  return entry;
}

function stopPollerIfIdle(key: string, entry: PollerEntry): void {
  if (
    entry.periodSubscribers.length === 0 &&
    entry.variantSubscribers.length === 0
  ) {
    clearInterval(entry.tickIntervalId!);
    orgPollers.delete(key);
    currentPeriodState.delete(key);
    scheduleVariants.delete(key);
    releaseScheduleSocketIfIdle();
  }
}

/**
 * Subscribe to real-time period updates for an organization. Shares an
 * entry with subscribeToScheduleVariants for the same org (see orgPollers
 * above).
 * @param orgId - Organization ID
 * @param callback - Callback when period state changes
 * @return Unsubscribe function
 */
export function subscribeToPeriodUpdates(
  orgId: OrgId | null | undefined,
  callback: PeriodCallback
): () => void {
  if (!orgId) {
    console.warn('⚠️ Cannot subscribe to period updates without orgId');
    return () => {};
  }

  const key = String(orgId);
  const entry = getOrCreatePoller(orgId, key);
  entry.periodSubscribers.push(callback);

  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    entry.periodSubscribers = entry.periodSubscribers.filter(
      (cb) => cb !== callback
    );
    stopPollerIfIdle(key, entry);
  };
}

/**
 * Subscribe to real-time schedule variant updates for an organization.
 * Shares an entry with subscribeToPeriodUpdates for the same org (see
 * orgPollers above).
 * @param orgId - Organization ID
 * @param callback - Callback when schedule variants change
 * @return Unsubscribe function
 */
export function subscribeToScheduleVariants(
  orgId: OrgId | null | undefined,
  callback: VariantCallback
): () => void {
  if (!orgId) {
    console.warn('⚠️ Cannot subscribe to schedule variants without orgId');
    return () => {};
  }

  const key = String(orgId);
  const entry = getOrCreatePoller(orgId, key);
  entry.variantSubscribers.push(callback);

  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    entry.variantSubscribers = entry.variantSubscribers.filter(
      (cb) => cb !== callback
    );
    stopPollerIfIdle(key, entry);
  };
}

/**
 * Get current period state from real-time cache
 * @param orgId - Organization ID
 * @return Current period state or null if not available
 */
export function getCurrentPeriodState(
  orgId: OrgId | null | undefined
): PeriodData | null {
  if (!orgId) {
    console.warn('⚠️ Cannot get period state without orgId');
    return null;
  }

  return currentPeriodState.get(String(orgId)) || null;
}

/**
 * Get schedule variants from real-time cache
 * @param orgId - Organization ID
 * @return Schedule variants object (empty if not available)
 */
export function getScheduleVariants(orgId: OrgId | null | undefined): Variants {
  if (!orgId) {
    console.warn('⚠️ Cannot get schedule variants without orgId');
    return {};
  }

  return scheduleVariants.get(String(orgId)) || {};
}

/**
 * Check if enough time remains for pass creation
 * @param currentPeriod - Current period object
 * @param minimumMinutes - Minimum minutes required
 * @return True if enough time remains
 */
export function hasEnoughTimeForPass(
  currentPeriod: { timeRemaining: number } | null,
  minimumMinutes: number = 15
): boolean | null {
  return currentPeriod && currentPeriod.timeRemaining >= minimumMinutes;
}

/**
 * Check if pass creation is allowed based on temporal restrictions
 * @param currentPeriod - Current period object
 * @param countsTowardLimit - Whether pass counts toward limit
 * @return {allowed: boolean, reason: string}
 */
export function isPassCreationAllowed(
  currentPeriod: CurrentPeriodInput | null,
  countsTowardLimit: boolean = true
): PassCreationResult {
  if (!currentPeriod) return { allowed: false, reason: 'No current period' };

  // Skip temporal restrictions for destinations that don't count toward limits
  if (!countsTowardLimit) {
    return {
      allowed: true,
      reason: 'Destination bypasses timing restrictions',
    };
  }

  // Get the standardized period name
  const periodName = standardizePeriodFormat(
    currentPeriod.period || currentPeriod.periodNumber
  );

  // hallpass-backend's schedule-types + periods model has no per-period
  // passBlockingStart/passBlockingEnd equivalent, so blackout windows are
  // hardcoded by period-name pattern rather than school-configurable.
  let blackoutConfig: BlackoutConfig;
  if (periodName === 'Advisory') {
    blackoutConfig = { passBlockingStart: 5, passBlockingEnd: 5 };
  } else if (periodName && periodName.match(/^Period \d+$/)) {
    blackoutConfig = { passBlockingStart: 15, passBlockingEnd: 15 };
  } else if (periodName === 'Lunch') {
    blackoutConfig = { passBlockingStart: 0, passBlockingEnd: 0 };
  } else {
    blackoutConfig = { passBlockingStart: 0, passBlockingEnd: 0 };
  }

  // Calculate minutes into period and time remaining
  const totalPeriodMinutes = currentPeriod.minutes || 85; // Default period length
  const timeRemaining = currentPeriod.timeRemaining || 0;
  const minutesIntoPeriod = Math.max(0, totalPeriodMinutes - timeRemaining);

  // Check start blackout period
  if (
    blackoutConfig.passBlockingStart > 0 &&
    minutesIntoPeriod < blackoutConfig.passBlockingStart
  ) {
    return {
      allowed: false,
      reason: `Cannot create passes during the first ${blackoutConfig.passBlockingStart} minutes of ${periodName}`,
    };
  }

  // Check end blackout period
  if (
    blackoutConfig.passBlockingEnd > 0 &&
    timeRemaining < blackoutConfig.passBlockingEnd
  ) {
    return {
      allowed: false,
      reason: `Cannot create passes during the last ${blackoutConfig.passBlockingEnd} minutes of ${periodName}`,
    };
  }

  // All timing checks passed
  return { allowed: true, reason: '' };
}

/**
 * Clean up all listeners for an organization
 * @param orgId - Organization ID
 */
export function unsubscribeFromOrg(orgId: OrgId): void {
  const key = String(orgId);

  const entry = orgPollers.get(key);
  if (entry) {
    clearInterval(entry.tickIntervalId!);
    orgPollers.delete(key);
  }

  currentPeriodState.delete(key);
  scheduleVariants.delete(key);
  releaseScheduleSocketIfIdle();
}

/**
 * Clean up all listeners
 */
export function unsubscribeAll(): void {
  orgPollers.forEach((entry) => clearInterval(entry.tickIntervalId!));

  orgPollers.clear();
  currentPeriodState.clear();
  scheduleVariants.clear();
  releaseScheduleSocketIfIdle();
}

export { timeToMinutes } from './periodFormatHelper';
